import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';
import { Container } from 'src/app/storage/models/container.model';
import { environment } from 'src/environments/environment';
import { ContainerMission } from 'src/app/storage/models/container-mission.model';
import { IContainerMission } from 'src/app/storage/interfaces/container-mission.interface';
import { ApiService } from './api.service';
import { IContainer } from 'src/app/storage/interfaces/container.interface';
import { IContainerModel } from 'src/app/storage/interfaces/container-model.interface';
import { ITransportCompany } from 'src/app/storage/interfaces/transport-company.interface';

const URL = environment.aws.amplify.API.REST;

@Injectable({
  providedIn: 'root'
})
export class ContainersService {

  missionContainers$: BehaviorSubject<ContainerMission[]> = new BehaviorSubject(null);

  constructor(
    private translateService: TranslateService,
    private apiService: ApiService
  ) { }

  async getContainers(marketname: string, missionId: string): Promise<Container[] | null> {
    const translations = await this.translateService.get('CONTAINERS').toPromise();

    const response = await this.apiService.get(
      `${URL}containers/market/${marketname}/${missionId}`,
      translations['LABEL'],
      { showLoader: true }
    );

    return response ?
          response.containers.map((container: IContainer) => new Container(container)) :
          null ;
  }

  async getMarketContainers(marketname: string): Promise<Container[] | null> {
    const translations = await this.translateService.get('CONTAINERS').toPromise();

    const response = await this.apiService.get(
      `${URL}containers/market/${marketname}`,
      translations['LABEL'],
      { showLoader: true }
    );

    return response ?
          response.containers.map((container: IContainer) => new Container(container)) :
          null ;
  }

  async addContainer(marketname: string, container: Container): Promise<boolean> {
    const translations = await this.translateService.get('CONTAINERS').toPromise();

    const payload = {
      containerId: container.containerId,
      containerTareWeight: container.containerTareWeight,
      containerMAWP: container.containerMAWP,
      containerMaxCapacity: container.containerMaxCapacity,
      containerPumpCarrier: container.containerPumpCarrier,
      transportcompanyName: container.transportcompanyName,
      containermodelName: container.containermodelName,
      deletionFlag: container.deletionFlag
    };

    const response = await this.apiService.put(
      `${URL}containers/market/${marketname}`,
      payload,
      translations['LABEL'],
      { showLoader: true }
    );

    return response != null;
  }

  async updateContainer(marketname: string, container: Container): Promise<boolean> {
    const translations = await this.translateService.get('CONTAINERS').toPromise();

    const payload = {
      containerId: container.containerId,
      containerTareWeight: container.containerTareWeight,
      containerMAWP: container.containerMAWP,
      containerMaxCapacity: container.containerMaxCapacity,
      containerPumpCarrier: container.containerPumpCarrier,
      transportcompanyName: container.transportcompanyName,
      containermodelName: container.containermodelName,
      deletionFlag: container.deletionFlag
    };

    const response = await this.apiService.post(
      `${URL}containers/market/${marketname}/${container.containerpk}`,
      payload,
      translations['LABEL'],
      { showLoader: true }
    );

    return response != null;
  }

  async removeContainer(marketname: string, container: Container): Promise<boolean> {
    const translations = await this.translateService.get('CONTAINERS').toPromise();

    const response = await this.apiService.delete(
      `${URL}containers/market/${marketname}/${container.containerpk}`,
      translations['LABEL'],
      { showLoader: true }
    );

    return response != null;
  }

  async getMissionContainers(missionId: string): Promise<ContainerMission[] | null> {
    const translations = await this.translateService.get('CONTAINERS').toPromise();

    const response = await this.apiService.get(
      `${URL}containers/mission/${missionId}`,
      translations['LABEL']
    );

    return response ?
          response.missionContainers.map((missionContainer: IContainerMission) => new ContainerMission(missionContainer)) :
          null ;
  }

  async addMissionContainer(missionId: string, missionContainer: ContainerMission): Promise<boolean> {
    const translations = await this.translateService.get('CONTAINERS').toPromise();

    const payload = {
      containerpk: missionContainer.containerpk,
      containermissionLoadedDMDSQuantity: missionContainer.containermissionLoadedDMDSQuantity,
      loadingStationName: missionContainer.loadingstationName,
      productName: missionContainer.productName
    };

    const response = await this.apiService.put(
      `${URL}containers/mission/${missionId}`,
      payload,
      translations['LABEL'],
      { showLoader: true }
    );

    return response != null;
  }

  async updateMissionContainer(missionId: string, missionContainer: ContainerMission): Promise<boolean> {
    const translations = await this.translateService.get('CONTAINERS').toPromise();

    const payload = {
      containerpk: missionContainer.containerpk,
      containermissionLoadedDMDSQuantity: missionContainer.containermissionLoadedDMDSQuantity,
      loadingStationName: missionContainer.loadingstationName,
      productName: missionContainer.productName
    };

    const response = await this.apiService.post(
      `${URL}containers/mission/${missionId}/${missionContainer.containerpk}`,
      payload,
      translations['LABEL'],
      { showLoader: true }
    );

    return response != null;
  }

  async removeMissionContainer(missionId: string, missionContainer: ContainerMission): Promise<boolean> {
    const translations = await this.translateService.get('CONTAINERS').toPromise();

    const response = await this.apiService.delete(
      `${URL}containers/mission/${missionId}/${missionContainer.containerpk}`,
      translations['LABEL'],
      { showLoader: true }
    );

    return response != null;
  }

  async getContainerModels(): Promise<IContainerModel[] | null> {
    const translations = await this.translateService.get('CONTAINERS').toPromise();

    const response = await this.apiService.get(
      `${URL}containers/models`,
      translations['LABEL'],
      { showLoader: true }
    );

    return response ? response.containerModels : null;
  }

  async getTransportCompanies(): Promise<ITransportCompany[] | null> {
    const translations = await this.translateService.get('CONTAINERS').toPromise();

    const response = await this.apiService.get(
      `${URL}containers/transport-companies`,
      translations['LABEL'],
      { showLoader: true }
    );

    return response ? response.transportCompanies : null;
  }
}
